import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, ClipboardCheck, ArrowRight } from 'lucide-react'
import { FeaturedLayout } from '../../layouts'
import {
  Button, Card, CardContent, Badge, Input, Label,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from '../../ui'
import { getConteos, iniciarConteo } from '../../../lib/supabase'
import { useConfirmDialog } from '../../../hooks/useConfirmDialog'
import { useUnsavedGuard } from '../../../hooks/useUnsavedGuard'

const ESTADO_LABELS = {
  abierto: 'Abierto',
  cerrado: 'Cerrado',
}

const InventarioConteos = () => {
  const navigate = useNavigate()
  const { alert, ConfirmDialog } = useConfirmDialog()
  const guard = useUnsavedGuard({ onClose: () => setDialogOpen(false) })
  const [conteos, setConteos] = useState([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [nombre, setNombre] = useState('')
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await getConteos()
    setConteos(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const abrirDialogo = () => {
    setNombre('')
    guard.resetDirty()
    setDialogOpen(true)
  }

  const handleIniciar = async () => {
    setSaving(true)
    const { data, error } = await iniciarConteo({ nombre: nombre.trim() || null })
    setSaving(false)
    if (error || !data) {
      await alert('Error', error?.message || 'No se pudo iniciar el conteo')
      return
    }
    guard.resetDirty()
    setDialogOpen(false)
    navigate('/admin/inventario/conteos/' + data.id)
  }

  const etiquetaConteo = (c) => c.nombre?.trim() || `Conteo ${c.fecha || ''}`.trim()

  return (
    <FeaturedLayout
      title="Conteos"
      subtitle="Toma física de inventario"
      refreshAction={load}
    >
      <div className="mb-4 flex items-center justify-between gap-2">
        <span className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{conteos.length}</span> conteos
        </span>
        <Button onClick={abrirDialogo}>
          <Plus className="h-4 w-4 mr-2" /> Iniciar conteo
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm py-8 text-center">Cargando...</p>
      ) : conteos.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ClipboardCheck className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground mb-4">Aún no has hecho ningún conteo físico</p>
            <Button onClick={abrirDialogo}>
              <Plus className="h-4 w-4 mr-2" /> Iniciar conteo
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Conteo</TableHead>
                <TableHead className="hidden sm:table-cell">Fecha</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="w-28 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {conteos.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <span className="font-medium">{etiquetaConteo(c)}</span>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">
                    {c.fecha || '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={c.estado === 'cerrado' ? 'secondary' : 'default'} className="text-xs">
                      {ESTADO_LABELS[c.estado] || c.estado}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/admin/inventario/conteos/' + c.id)}
                      >
                        <span className="hidden sm:inline">Abrir</span>
                        <ArrowRight className="h-3.5 w-3.5 sm:ml-1" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={guard.onOpenChange}>
        <DialogContent {...guard.dirtyProps}>
          <DialogHeader>
            <DialogTitle>Iniciar conteo</DialogTitle>
            <DialogDescription>
              Se tomará una foto de las cantidades actuales para capturar lo realmente contado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="conteo-nombre">Nombre (opcional)</Label>
            <Input
              id="conteo-nombre"
              placeholder="Ej: Conteo de junio"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => guard.onOpenChange(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleIniciar} disabled={saving}>
              {saving ? 'Iniciando...' : 'Iniciar conteo'}
            </Button>
          </DialogFooter>
          {guard.DiscardDialog}
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </FeaturedLayout>
  )
}

export default InventarioConteos
